import { animationRotate } from '../utils';
import { Icon, LinkIcon, Pane, PauseIcon, PlayIcon } from 'evergreen-ui';
import { useCallback, useEffect, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';
import styled, { keyframes } from 'styled-components';

const MIN = 0;
const MAX = 100;
const SIZE = 64;

const swipeAnimation = keyframes`
    0%{ background-position: 200% 50% }
    100%{ background-position: 0% 50% }
`;

const StyledSwipeAnimation = styled.div`
  margin: 0 -${SIZE}px;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ffffff;
  position: relative;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0) 40%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0) 52%,
    rgba(255, 255, 255, 0) 100%
  );
  background-size: 400% 100%;
  animation: ${swipeAnimation} 2s linear infinite;
`;

const StyledLoadingAnimation = styled.svg`
  position: absolute;
  animation: ${animationRotate} 2s linear infinite;
  transition: all 0.5s;

  &.loading {
    opacity: 1;
    width: 36px;
  }
`;
const LoadingAnimation = ({ status }) => (
  <StyledLoadingAnimation
    className={status}
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.1699 25.2331C1.66182 22.1411 1.15791 18.655 1.72867 15.2625C2.29943 11.87 3.9163 8.74072 6.35312 6.31239C8.78994 3.88406 11.9248 2.27812 15.3193 1.71921C18.7138 1.1603 22.1981 1.67637 25.2848 3.19524"
      stroke="#EFF1F6"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M33.736 13.0383C34.6377 15.8981 34.743 18.9495 34.0407 21.8647C33.3384 24.7798 31.8549 27.4485 29.7498 29.5838C27.6446 31.7192 24.9974 33.2405 22.0925 33.9843C19.1877 34.7281 16.135 34.6662 13.2627 33.8054"
      stroke="#EFF1F6"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </StyledLoadingAnimation>
);

const SlideToStart = ({ onSlided, searching, onPause, selectedTab, isLinked }) => {
  const [slided, setSlided] = useState([0]);

  const updateSlided = values => {
    if (values[0] !== 100) {
      setSlided([0]);
    } else {
      onSlided();
    }
  };

  const pause = useCallback(() => {
    if (searching) onPause();
  }, [searching, onPause]);

  useEffect(() => {
    setSlided([0]);
  }, [selectedTab, searching]);

  const slideAchieved = slided[0] === 100;

  return (
    <Pane
      position="relative"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width={500}
      maxWidth="calc(100vw - 40px)"
    >
      <Range
        disabled={slideAchieved}
        values={slided}
        step={0.1}
        min={MIN}
        max={MAX}
        onChange={setSlided}
        onFinalChange={updateSlided}
        renderTrack={({ props, children }) => (
          <div
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            style={{
              ...props.style,
              height: SIZE,
              display: 'flex',
              padding: searching ? 0 : `0 ${SIZE / 2}px`,
              width: searching ? SIZE : '100%',
              opacity: searching ? 0 : 1,
              overflow: 'hidden',
              borderRadius: SIZE,
              border: ' 4px #3E4143',
              transition: 'all .333s ease',
            }}
          >
            <div
              ref={props.ref}
              style={{
                height: SIZE,
                width: '100%',
                background: getTrackBackground({
                  values: slided,
                  colors: ['#E78850', '#3E4143'],
                  min: MIN,
                  max: MAX,
                }),
                boxShadow: `-${SIZE / 2}px 0 0 #E78850,${SIZE / 2}px 0 0 #3E4143`,
                alignSelf: 'center',
              }}
            >
              {children}
              <StyledSwipeAnimation style={{ opacity: 1 - slided[0] / 40 }}>
                {isLinked ? 'SLIDE TO START' : 'SLIDE TO RE-LINK'}
              </StyledSwipeAnimation>
            </div>
          </div>
        )}
        renderThumb={({ props, isDragged }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: SIZE,
              width: SIZE,
              backgroundColor: '#E78850',
              borderRadius: SIZE,
              outline: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {slideAchieved ? (
              <LoadingAnimation />
            ) : isLinked ? (
              <Icon icon={PlayIcon} size={28} color="#FFFFFF" />
            ) : (
              <Icon icon={LinkIcon} size={28} color="#FFFFFF" />
            )}
          </div>
        )}
      />
      <div
        style={{
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: SIZE,
          height: SIZE,
          borderRadius: SIZE,
          background: '#3E4143',
          opacity: searching ? 1 : 0,
          transition: 'all .333s ease',
          cursor: 'pointer',
        }}
        onClick={pause}
      >
        <Icon icon={PauseIcon} size={24} color="#A7AAAC" />
      </div>
    </Pane>
  );
};

export default SlideToStart;
